import React, { useMemo } from 'react';
import Select from 'react-select';
import countries from 'i18n-iso-countries';
import en from 'i18n-iso-countries/langs/en.json';
import { Search } from 'lucide-react';

// Initialize the countries library with English language
countries.registerLocale(en);

interface CountrySelectProps {
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
  error?: string;
}

export function CountrySelect({ value, onChange, required = false, error }: CountrySelectProps) {
  // Get all countries and create options array
  const options = useMemo(() => {
    const countryObj = countries.getNames('en', { select: 'official' });
    return Object.entries(countryObj)
      .map(([code, name]) => ({
        value: code,
        label: name
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, []);

  // Find the current selected option
  const selectedOption = options.find(option => option.value === value);

  const customStyles = {
    control: (base: any, state: { isFocused: boolean }) => ({
      ...base,
      minHeight: '42px',
      backgroundColor: 'var(--color-surface-alt)',
      borderColor: error ? '#FCA5A5' : state.isFocused ? 'var(--color-border-focus)' : 'var(--color-border)',
      boxShadow: state.isFocused ? `0 0 0 2px var(--color-border-focus)` : 'none',
      '&:hover': {
        borderColor: error ? '#FCA5A5' : 'var(--color-border-focus)'
      }
    }),
    option: (base: any, state: { isSelected: boolean; isFocused: boolean }) => ({
      ...base,
      backgroundColor: state.isSelected 
        ? 'var(--color-primary)' 
        : state.isFocused 
          ? 'var(--color-surface-alt)' 
          : 'transparent',
      color: state.isSelected ? 'white' : 'var(--color-text)',
      cursor: 'pointer',
      '&:active': {
        backgroundColor: 'var(--color-primary)'
      }
    }),
    input: (base: any) => ({
      ...base,
      color: 'var(--color-text)'
    }),
    singleValue: (base: any) => ({
      ...base,
      color: 'var(--color-text)'
    }),
    menu: (base: any) => ({
      ...base,
      backgroundColor: 'var(--color-surface)',
      boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)'
    }),
    dropdownIndicator: (base: any) => ({
      ...base,
      color: 'var(--color-text-secondary)',
      '&:hover': {
        color: 'var(--color-text)'
      }
    })
  };

  return (
    <div>
      <Select
        value={selectedOption}
        onChange={(option) => onChange(option?.value || '')}
        options={options}
        isClearable
        isSearchable
        placeholder="Search countries..."
        required={required}
        styles={customStyles}
        components={{
          DropdownIndicator: () => (
            <div className="px-2">
              <Search className="h-4 w-4 text-gray-400" />
            </div>
          )
        }}
      />
      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
    </div>
  );
}